import React, {useEffect, useState} from "react";
import useSWR from "swr";
import {
    AccountingPeriodList,
    AccountingPeriodRestControllerApiFactory,
    JournalEntryList,
    JournalEntryRestControllerApiFactory,
    JournalLineList,
    JournalSuggestionDto,
} from "arteco-api-client-ts";
import {
    Box,
    Button,
    Fab,
    FormControl,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import {useApiManager} from "../../../../components/utils/api";
import {useNavigate} from "react-router-dom";
import {dateToString} from "../../../../components/utils/dates";
import {fmtAmt, fmtAmtNz, fmtAccountCode} from "../../../../components/utils/numbers";
import {
    EmptyState,
    FilterChips,
    Page,
    PageHeader,
    Panel,
    SearchField,
    SkeletonRows,
    SoftChip,
    StatGrid,
    StatTile,
    tableContainerSx,
    tableSx,
    Toolbar,
    Tone,
} from "../../../../components/kit";

const statusTone: Record<string, Tone> = {
    BORRADOR: "warning",
    CONFIRMADO: "success",
    ANULADO: "error",
};

const statusLabel: Record<string, string> = {
    BORRADOR: "Borrador",
    CONFIRMADO: "Confirmado",
    ANULADO: "Anulado",
};

const StatusChip = ({status}: { status: string }) =>
    <SoftChip tone={statusTone[status] ?? "neutral"} label={statusLabel[status] ?? status}/>;

// Firefox lanza NS_ERROR_FAILURE al acceder a localStorage si el almacenamiento
// está bloqueado para el sitio (protección de rastreo estricta, bloqueo de cookies…).
// Envolvemos los accesos para que eso no tumbe la pantalla.
const safeGetItem = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const safeSetItem = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // almacenamiento no disponible: ignoramos la persistencia
    }
};

// Período elegido en el diario durante esta sesión, para que al volver de un asiento la caché se
// muestre al instante sin esperar a preguntar otra vez por el período actual (#13895).
let rememberedPeriod: { id?: number } | undefined;

type Layer = "oficial" | "extranet" | "todas";
let rememberedLayer: Layer = "oficial";
const LAYER_HINT: Record<Layer, string> = {
    oficial: "Asientos confirmados de la fuente oficial del ejercicio",
    extranet: "Asientos confirmados anotados en la Extranet",
    todas: "Contasol y Extranet sumados: cuenta dos veces lo que está en las dos",
};

export const JournalEntryListPage = () => {
    const apiManager = useApiManager();
    const navigate = useNavigate();
    const [onlyDraft, setOnlyDraft] = useState(false);
    const [suggestions, setSuggestions] = useState<JournalSuggestionDto[]>([]);
    const [periods, setPeriods] = useState<AccountingPeriodList[]>([]);
    const [selectedPeriodId, setSelectedPeriodIdState] = useState<number | undefined>(rememberedPeriod?.id);
    // Hasta saber el período no se pide la lista (#13895): antes salían dos peticiones de 667 KB, una
    // sin período y otra con él, y ganaba la que llegara última. Al volver de un asiento ya se sabe.
    const [periodResolved, setPeriodResolved] = useState(rememberedPeriod !== undefined);
    const setSelectedPeriodId = (id: number | undefined) => {
        rememberedPeriod = {id};
        setSelectedPeriodIdState(id);
    };

    const [layer, setLayerState] = useState<Layer>(rememberedLayer);
    const setLayer = (l: Layer) => { rememberedLayer = l; setLayerState(l); };
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [search, setSearch] = useState("");
    const [sortDesc, setSortDesc] = useState<boolean>(
        () => safeGetItem("journal-sort-desc") !== "false"
    );

    const toggleSort = () => {
        setSortDesc(prev => {
            const next = !prev;
            safeSetItem("journal-sort-desc", String(next));
            return next;
        });
    };

    useEffect(() => {
        const ctrl = apiManager.factory(JournalEntryRestControllerApiFactory);
        apiManager.execute(ctrl.suggestionsJournalEntry())
            .then((list) => setSuggestions(list as JournalSuggestionDto[]))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const periodCtrl = apiManager.factory(AccountingPeriodRestControllerApiFactory);
        Promise.all([
            apiManager.execute(periodCtrl.listAccountingPeriod()),
            apiManager.execute(periodCtrl.currentAccountingPeriod()),
        ]).then(([list, current]) => {
            setPeriods(list as AccountingPeriodList[]);
            if (rememberedPeriod === undefined) setSelectedPeriodId((current as any)?.id || undefined);
        }).catch(() => {}).finally(() => setPeriodResolved(true));
    }, []);

    // Caché con revalidación (#13895): al volver al diario desde un asiento se ve al instante lo
    // último cargado y se refresca por detrás. La clave es el período y el filtro; hasta saber el
    // período actual no se pide nada (antes salían dos peticiones y ganaba la última).
    const status = onlyDraft ? "BORRADOR" as any : undefined;
    const {data, isLoading, mutate} = useSWR(
        periodResolved ? ["journal.list", selectedPeriodId ?? null, onlyDraft] : null,
        () => apiManager.execute(apiManager.factory(JournalEntryRestControllerApiFactory)
            .listJournalEntry(selectedPeriodId, status, undefined, undefined, undefined, undefined, false))
            .then(list => list as JournalEntryList[]),
        {revalidateOnFocus: false, keepPreviousData: false},
    );
    const entries: JournalEntryList[] = data || [];
    const loading = !periodResolved || (isLoading && !data);

    const newEntry = (suggestion?: JournalSuggestionDto) =>
        navigate("/app/admin/accounting/journal/new", {state: {suggestion}});

    const deleteEntry = (entry: JournalEntryList, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!confirm(`¿Eliminar asiento "${entry.description}"?`)) return;
        const ctrl = apiManager.factory(JournalEntryRestControllerApiFactory);
        apiManager.execute(ctrl.deleteOrDismissJournalEntry(entry.id!), "Asiento eliminado")
            .then(() => mutate(prev => (prev || []).filter(en => en.id !== entry.id), {revalidate: false}));
    };

    const needle = search.trim().toLowerCase();
    // Orden cronológico por fecha de asiento (lo que pide el Libro Diario). Como el
    // entryNumber se asigna por fecha al renumerar, número y fecha van alineados; se usa
    // como desempate. Los borradores (aún sin número) flotan arriba, fuera de la secuencia.
    const sortedEntries = entries.slice().sort((a, b) => {
        const aDraft = a.entryNumber == null;
        const bDraft = b.entryNumber == null;
        if (aDraft !== bDraft) return aDraft ? -1 : 1;
        const aDate = a.entryDate ?? "";
        const bDate = b.entryDate ?? "";
        if (aDate !== bDate) return sortDesc ? bDate.localeCompare(aDate) : aDate.localeCompare(bDate);
        const aNum = a.entryNumber ?? 0;
        const bNum = b.entryNumber ?? 0;
        return sortDesc ? bNum - aNum : aNum - bNum;
    });
    // Capa (#14701): mientras conviven, el mismo hecho está importado de Contasol y anotado aquí. Lo
    // oficial es la fuente del ejercicio; sumar las dos daba un diario de 6,8 M frente a los 4,9 M de
    // Contasol. Los borradores se ven siempre (hay que confirmarlos), pero no suman.
    const periodOf = (e: JournalEntryList) => periods.find(p => p.id === (e.period as any)?.id);
    // Desde el corte del ejercicio (extranetFrom, #14784) cuenta la Extranet aunque la fuente sea Contasol.
    const sourceOf = (e: JournalEntryList) => {
        const from = (e.period as any)?.extranetFrom ?? periodOf(e)?.extranetFrom;
        if (from && (e.entryDate ?? "") >= from) return "EXTRANET";
        return (e.period as any)?.officialSource ?? periodOf(e)?.officialSource;
    };
    const inLayer = (e: JournalEntryList) => {
        if (e.status === "BORRADOR" || layer === "todas") return true;
        const contasol = e.origin === "CONTASOL";
        if (layer === "extranet") return !contasol;
        const src = sourceOf(e);
        return !src || (src === "CONTASOL" ? contasol : !contasol);
    };
    const layerEntries = sortedEntries.filter(inLayer);
    const filteredEntries = needle === "" ? layerEntries : layerEntries.filter(entry => {
        if ((entry.description ?? "").toLowerCase().includes(needle)) return true;
        if (String(entry.entryNumber ?? "").includes(needle)) return true;
        return (entry.lines ?? []).some(l =>
            (l.description ?? "").toLowerCase().includes(needle) ||
            ((l.account as any)?.code ?? "").toLowerCase().includes(needle) ||
            ((l.account as any)?.name ?? "").toLowerCase().includes(needle)
        );
    });

    const allLines = filteredEntries.filter(e => e.status === "CONFIRMADO").flatMap(e => e.lines ?? []);
    // Se suma en céntimos enteros: sumando euros en coma flotante, dos totales iguales que caen en
    // medio céntimo (…,905) pueden redondear uno a ,90 y el otro a ,91 según el orden (#13571).
    const cents = (v?: number) => Math.round((v ?? 0) * 100);
    const totalDebe = allLines.reduce((s, l) => s + cents(l.debit), 0) / 100;
    const totalHaber = allLines.reduce((s, l) => s + cents(l.credit), 0) / 100;
    const balanced = totalDebe === totalHaber;

    const drafts = entries.filter(e => e.status === "BORRADOR").length;
    const periodName = periods.find(p => p.id === selectedPeriodId)?.name;
    const amountSx = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", verticalAlign: "top"} as const;

    const TotalsRow = ({position}: { position: "top" | "bottom" }) => (
        <TableRow sx={{
            "& > td": {
                bgcolor: "grey.50", fontWeight: 700, color: "text.primary",
                ...(position === "top" ? {borderBottom: "2px solid", borderColor: "divider"} : {borderTop: "2px solid", borderColor: "divider"}),
            },
        }}>
            <TableCell colSpan={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <span>Totales</span>
                    {!balanced && <SoftChip tone="error" label="Descuadrado"/>}
                </Stack>
            </TableCell>
            <TableCell align="right" sx={amountSx}>{fmtAmt(totalDebe)}</TableCell>
            <TableCell align="right" sx={amountSx}>{fmtAmt(totalHaber)}</TableCell>
            <TableCell colSpan={2}/>
        </TableRow>
    );

    return (
        <Page maxWidth={1400}>
            <PageHeader
                title="Libro diario"
                subtitle={`${filteredEntries.length} ${filteredEntries.length === 1 ? "asiento" : "asientos"}${periodName ? " · " + periodName : ""}`}
                actions={<>
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon/>}
                        endIcon={suggestions.length > 0 ? <KeyboardArrowDownRoundedIcon/> : undefined}
                        onClick={(e) => suggestions.length > 0 ? setMenuAnchor(e.currentTarget) : newEntry()}
                    >
                        Nuevo asiento
                    </Button>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                        <MenuItem onClick={() => { setMenuAnchor(null); newEntry(undefined); }}>En blanco</MenuItem>
                        {suggestions.map((s, i) => (
                            <MenuItem key={i} onClick={() => { setMenuAnchor(null); newEntry(s); }}>
                                {s.label} ({s.occurrences})
                            </MenuItem>
                        ))}
                    </Menu>
                </>}
            />

            <StatGrid>
                <StatTile label="Asientos" value={filteredEntries.length} loading={loading}/>
                <StatTile label="Borradores" value={drafts} tone={drafts > 0 ? "warning" : "neutral"} loading={loading}/>
                <StatTile label="Debe" value={fmtAmt(totalDebe)} loading={loading}
                          hint={!loading && <Box sx={{mt: 0.5, color: "text.secondary", fontSize: 12}}>{LAYER_HINT[layer]}</Box>}/>
                <StatTile label="Haber" value={fmtAmt(totalHaber)} loading={loading}
                          hint={!loading && <Box sx={{mt: 0.5}}>
                              {balanced ? <SoftChip tone="success" label="Cuadra"/> : <SoftChip tone="error" label="Descuadrado"/>}
                          </Box>}/>
            </StatGrid>

            <Panel>
                <Toolbar
                    left={
                        <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} alignItems={{sm: "center"}}>
                            <FormControl size="small" sx={{minWidth: 220}}>
                                <InputLabel>Período</InputLabel>
                                <Select
                                    value={selectedPeriodId ?? ""}
                                    label="Período"
                                    onChange={(e) => setSelectedPeriodId(e.target.value === "" ? undefined : Number(e.target.value))}
                                    sx={{borderRadius: 2}}
                                >
                                    <MenuItem value=""><em>Todos los períodos</em></MenuItem>
                                    {periods.map(p => (
                                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FilterChips value={layer} onChange={setLayer} options={[
                                {value: "oficial", label: "Oficial"},
                                {value: "extranet", label: "Anotado en la Extranet"},
                                {value: "todas", label: "Todo"},
                            ]}/>
                            <FilterChips value={onlyDraft} onChange={setOnlyDraft} options={[
                                {value: false, label: "Todos"},
                                {value: true, label: "Solo borradores"},
                            ]}/>
                        </Stack>
                    }
                    right={<>
                        <Tooltip title={sortDesc ? "Orden por fecha: más reciente primero" : "Orden por fecha: más antiguo primero"}>
                            <Button variant="outlined" size="small" onClick={toggleSort}
                                    startIcon={sortDesc ? <ArrowDownwardIcon/> : <ArrowUpwardIcon/>}
                                    sx={{whiteSpace: "nowrap"}}>
                                Fecha
                            </Button>
                        </Tooltip>
                        <SearchField value={search} onChange={setSearch} placeholder="Buscar asiento, cuenta o concepto"/>
                    </>}
                />

                {loading && <SkeletonRows rows={8} height={40}/>}

                {!loading && entries.length === 0 && (
                    <EmptyState icon={<MenuBookRoundedIcon/>} title="No hay asientos para el período seleccionado"/>
                )}

                {!loading && entries.length > 0 && filteredEntries.length === 0 && (
                    <EmptyState title="No hay asientos que coincidan con la búsqueda"/>
                )}

                {!loading && filteredEntries.length > 0 && (
                    // En móvil la tabla se desplaza dentro de su caja: un asiento se lee por columnas.
                    <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                    <Table size="small" sx={{...tableSx, width: "100%", minWidth: 900}}>
                        <TableHead>
                            <TableRow>
                                <TableCell width={60}>Nº</TableCell>
                                <TableCell width={100}>Fecha</TableCell>
                                <TableCell width={170}>Cuenta</TableCell>
                                <TableCell>Descripción</TableCell>
                                <TableCell width={120} align="right">Debe</TableCell>
                                <TableCell width={120} align="right">Haber</TableCell>
                                <TableCell width={120}>Estado</TableCell>
                                <TableCell width={50}/>
                            </TableRow>
                            <TotalsRow position="top"/>
                        </TableHead>
                        <TableBody>
                            {filteredEntries.map((entry, entryIdx) => {
                                const lines = entry.lines ?? [];
                                // Cada asiento en su franja: fondo alterno y raya arriba, para separar uno de otro.
                                const bgColor = entryIdx % 2 === 0 ? "background.paper" : "grey.50";
                                const borderTop = entryIdx > 0 ? "1px solid #d9dde3" : undefined;
                                const handleClick = () => navigate(`/app/admin/accounting/journal/${entry.id}`);
                                const deleteButton = entry.status !== "CONFIRMADO" && (
                                    <Tooltip title="Eliminar asiento">
                                        <IconButton size="small" color="error" onClick={(e) => deleteEntry(entry, e)}>
                                            <DeleteOutlineRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                );

                                if (lines.length === 0) {
                                    return (
                                        <TableRow
                                            key={`${entry.id}-empty`}
                                            hover
                                            sx={{cursor: "pointer", bgcolor: bgColor, "& > td": {borderTop}}}
                                            onClick={handleClick}
                                        >
                                            <TableCell sx={{fontFamily: "monospace"}}>{entry.entryNumber ?? "—"}</TableCell>
                                            <TableCell sx={amountSx}>{dateToString(entry.entryDate)}</TableCell>
                                            <TableCell/>
                                            <TableCell sx={{color: "text.secondary", fontStyle: "italic"}}>
                                                {entry.description}
                                            </TableCell>
                                            <TableCell/><TableCell/>
                                            <TableCell><StatusChip status={entry.status}/></TableCell>
                                            <TableCell>{deleteButton}</TableCell>
                                        </TableRow>
                                    );
                                }

                                return lines.map((line: JournalLineList, lineIdx: number) => {
                                    const isFirst = lineIdx === 0;
                                    return (
                                        <TableRow
                                            key={`${entry.id}-${line.id ?? lineIdx}`}
                                            hover
                                            sx={{
                                                cursor: "pointer",
                                                bgcolor: bgColor,
                                                "& > td": {borderTop: isFirst ? borderTop : undefined, borderBottom: 0},
                                            }}
                                            onClick={handleClick}
                                        >
                                            <TableCell sx={{fontFamily: "monospace", verticalAlign: "top", fontWeight: 600}}>
                                                {isFirst ? (entry.entryNumber ?? "—") : ""}
                                            </TableCell>
                                            <TableCell sx={amountSx}>
                                                {isFirst ? dateToString(entry.entryDate) : ""}
                                            </TableCell>
                                            <TableCell sx={{verticalAlign: "top"}}>
                                                <Box>
                                                    <Typography component="span" sx={{fontFamily: "monospace", fontSize: "0.8rem"}}>
                                                        {fmtAccountCode((line.account as any)?.code)}
                                                    </Typography>
                                                    <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                                        {(line.account as any)?.name ?? ""}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{verticalAlign: "top"}}>
                                                {line.description || entry.description}
                                            </TableCell>
                                            <TableCell align="right" sx={amountSx}>{fmtAmtNz(line.debit)}</TableCell>
                                            <TableCell align="right" sx={amountSx}>{fmtAmtNz(line.credit)}</TableCell>
                                            <TableCell sx={{verticalAlign: "top"}}>
                                                {isFirst && <StatusChip status={entry.status}/>}
                                            </TableCell>
                                            <TableCell sx={{verticalAlign: "top"}}>
                                                {isFirst && deleteButton}
                                            </TableCell>
                                        </TableRow>
                                    );
                                });
                            })}
                            <TotalsRow position="bottom"/>
                        </TableBody>
                    </Table>
                    </TableContainer>
                )}
            </Panel>

            <Tooltip title="Ir al final">
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({top: document.body.scrollHeight, behavior: "smooth"})}
                    sx={{position: "fixed", bottom: 128, right: 16, zIndex: 1200}}
                >
                    <ArrowDownwardIcon/>
                </Fab>
            </Tooltip>
            <Tooltip title="Ir al inicio">
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({top: 0, behavior: "smooth"})}
                    sx={{position: "fixed", bottom: 72, right: 16, zIndex: 1200}}
                >
                    <ArrowUpwardIcon/>
                </Fab>
            </Tooltip>
        </Page>
    );
};
